import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

export default function TeamCard({ member }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isAnimatingIn, setIsAnimatingIn] = useState(false)

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      // Trigger enter animation on next frame
      requestAnimationFrame(() => setIsAnimatingIn(true))
    } else {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      setIsAnimatingIn(false)
    }
    return () => { 
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }, [isModalOpen])

  const openModal = (e) => {
    e.stopPropagation()
    setIsModalOpen(true)
  }

  const closeModal = () => setIsModalOpen(false)

  const agentWaMessage = encodeURIComponent(
    `Hi! With reference to your website profile . I’m interested to know more about Victory Homes’ properties & your Services . Could you please contact me with available listings of Rent , Resale , Off-Plans investments?`
  )

  const socialLinks = [
    member.whatsapp && {
      href: `https://wa.me/${member.whatsapp.replace(/[^0-9]/g, '')}?text=${agentWaMessage}`,
      icon: 'fab fa-whatsapp',
      label: 'WhatsApp',
    },
    member.instagram && {
      href: member.instagram,
      icon: 'fab fa-instagram',
      label: 'Instagram',
      target: '_blank',
    },
    member.facebook && {
      href: member.facebook,
      icon: 'fab fa-facebook-f',
      label: 'Facebook',
      target: '_blank',
    },
    member.tiktok && {
      href: member.tiktok,
      icon: 'fab fa-tiktok',
      label: 'TikTok',
      target: '_blank',
    },
    member.threads && {
      href: member.threads,
      icon: 'fab fa-threads',
      label: 'Threads',
      target: '_blank',
    },
    member.linkedin && {
      href: member.linkedin,
      icon: 'fab fa-linkedin-in',
      label: 'LinkedIn',
      target: '_blank',
    },
    member.x && {
      href: member.x,
      icon: 'fab fa-x-twitter',
      label: 'X',
      target: '_blank',
    }
  ].filter(Boolean)

  // ── MODAL (portalled to document.body) ─────────────────────────────────────
  const modal = isModalOpen ? createPortal(
    <>
      <style>{`
        @keyframes team-modal-backdrop-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes team-modal-box-in {
          from { opacity: 0; transform: scale(0.94) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .team-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          box-sizing: border-box;
          background: rgba(0, 15, 30, 0.82);
          backdrop-filter: blur(8px);
          animation: team-modal-backdrop-in 0.22s ease both;
        }
        .team-modal-box {
          position: relative;
          background: #fff;
          border-radius: 16px;
          width: 100%;
          max-width: 850px;
          height: 85vh; /* Uniform height */
          max-height: 650px; /* Cap the height so it looks like a nice card */
          overflow: hidden; /* Hide outer scroll */
          display: flex;
          flex-direction: row;
          animation: team-modal-box-in 0.28s cubic-bezier(0.34,1.56,0.64,1) both;
        }
        /* Scrollbar styling */
        .team-modal-text::-webkit-scrollbar { width: 5px; }
        .team-modal-text::-webkit-scrollbar-track { background: transparent; }
        .team-modal-text::-webkit-scrollbar-thumb { background: rgba(197,160,89,0.35); border-radius: 99px; }

        .team-modal-img  { flex: 0 0 340px; height: 100%; position: relative; }
        .team-modal-text { 
          flex: 1; 
          padding: 40px; 
          min-width: 0; 
          display: flex;
          flex-direction: column;
        }

        @media (max-width: 700px) {
          .team-modal-box {
            flex-direction: column;
            height: 90vh;
            max-height: unset;
          }
          .team-modal-img  {
            flex: 0 0 320px !important;
            height: 320px !important;
            min-height: unset !important;
            border-radius: 16px 16px 0 0 !important;
          }
          .team-modal-text { padding: 24px 18px 28px; }
        }
      `}</style>

      {/* Backdrop — click to close */}
      <div
        className="team-modal-backdrop"
        onClick={closeModal}
      >
        {/* Stop click-propagation so clicking inside the box doesn't close */}
        <div
          className="team-modal-box"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={closeModal}
            aria-label="Close"
            style={{
              position: 'absolute', top: '14px', right: '14px',
              zIndex: 10, width: '38px', height: '38px', borderRadius: '50%',
              background: '#f4f7fb', border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: NAVY, fontSize: '18px',
              transition: 'background 0.2s, transform 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#e5e9f0'; e.currentTarget.style.transform = 'scale(1.08)' }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f4f7fb'; e.currentTarget.style.transform = 'scale(1)' }}
          >
            &times;
          </button>

          {/* Split layout */}
          <div style={{ display: 'flex', flexDirection: 'inherit', width: '100%', height: '100%' }}>

            {/* — Image panel — */}
            <div
              className="team-modal-img"
              style={{
                backgroundImage: `url('${member.image}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
                borderTopLeftRadius: '16px',
                borderBottomLeftRadius: '16px',
              }}
            >

            </div>

            {/* — Text panel — */}
            <div className="team-modal-text">
              {/* Header section (fixed) */}
              <div style={{ flexShrink: 0 }}>
                {/* Role badge */}
                <div style={{
                  display: 'inline-block', background: `rgba(197,160,89,0.1)`, color: GOLD,
                  fontFamily: FONT, fontSize: '11px', fontWeight: 600, letterSpacing: '1px',
                  padding: '6px 12px', borderRadius: '4px', textTransform: 'uppercase',
                  marginBottom: '12px',
                }}>
                  {member.role}
                </div>

                {/* Name */}
                <h3 style={{
                  fontFamily: FONT, fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 700,
                  color: NAVY, margin: '0 0 14px 0', lineHeight: 1.2,
                }}>
                  {member.name}
                </h3>

                {/* Languages */}
                {member.languages && member.languages.length > 0 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    flexWrap: 'wrap', marginBottom: '20px',
                  }}>
                    <span style={{
                      fontFamily: FONT, fontSize: '11px', fontWeight: 600,
                      color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px',
                      flexShrink: 0,
                    }}>
                      <i className="fas fa-globe" style={{ marginRight: '5px', color: GOLD }}></i>
                      Languages:
                    </span>
                    {member.languages.map(lang => (
                      <span key={lang} style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: FONT, fontSize: '12px', fontWeight: 500,
                        color: NAVY,
                        backgroundColor: `rgba(197,160,89,0.1)`,
                        border: `1px solid rgba(197,160,89,0.35)`,
                        padding: '4px 12px', borderRadius: '20px',
                        lineHeight: 1, whiteSpace: 'nowrap'
                      }}>
                        {lang}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Full Bio (scrollable) */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                fontFamily: FONT, fontSize: '15px', fontWeight: 400,
                color: '#4b5563', lineHeight: '1.75',
                whiteSpace: 'pre-line',
                paddingRight: '10px',
                marginBottom: '20px',
              }}>
                {member.fullBio}
              </div>

              {/* Footer (fixed) */}
              <div style={{
                flexShrink: 0,
                paddingTop: '20px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap',
              }}>
                <a href="/contact" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  color: GOLD, fontFamily: FONT, fontSize: '14px', fontWeight: 600,
                  textDecoration: 'none', transition: 'opacity 0.2s',
                }}
                  onMouseEnter={e => e.currentTarget.style.opacity = 0.75}
                  onMouseLeave={e => e.currentTarget.style.opacity = 1}
                >
                  Contact {member.name.split(' ')[0]} →
                </a>

                {/* Social icon buttons — all gold to match brand */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginLeft: 'auto', justifyContent: 'flex-end', marginTop: '10px' }}>
                  {socialLinks.map(({ href, icon, label, target = '_blank' }) => (
                    <a
                      key={label}
                      href={href}
                      target={target}
                      rel="noreferrer"
                      title={label}
                      style={{
                        width: '40px', height: '40px', borderRadius: '8px',
                        backgroundColor: GOLD,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '17px', textDecoration: 'none',
                        transition: 'background 0.2s, transform 0.15s',
                        boxShadow: '0 2px 8px rgba(197,160,89,0.3)',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#d4af6a'
                        e.currentTarget.style.transform = 'scale(1.08)'
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = GOLD
                        e.currentTarget.style.transform = 'scale(1)'
                      }}
                    >
                      <i className={icon}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  ) : null

  // ── CARD ────────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={openModal}
        style={{
          position: 'relative',
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          boxShadow: isHovered ? '0 15px 35px rgba(0,0,0,0.12)' : '0 4px 15px rgba(0,0,0,0.05)',
          transform: isHovered ? 'translateY(-5px)' : 'translateY(0)',
          transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
          background: '#fff',
          flexShrink: 0,
        }}
      >
        {/* Photo */}
        <div style={{
          width: '100%',
          height: '380px',
          backgroundImage: `url('${member.image}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          transition: 'transform 0.5s ease',
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
        }} />

        {/* Social quick-action row — top right corner */}
        <div
          style={{
            position: 'absolute', top: '14px', right: '14px',
            display: 'flex', flexDirection: 'column', gap: '8px',
            zIndex: 10,
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* WhatsApp */}
          {member.whatsapp && (
            <a
              href={`https://wa.me/${member.whatsapp.replace(/[^0-9]/g, '')}?text=${agentWaMessage}`}
              target="_blank"
              rel="noreferrer"
              style={{
                width: '40px', height: '40px', borderRadius: '50%',
                backgroundColor: GOLD, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px', textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                transition: 'transform 0.2s, background 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.background = '#d4af6a' }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = GOLD }}
              title={`WhatsApp ${member.name.split(' ')[0]}`}
            >
              <i className="fab fa-whatsapp"></i>
            </a>
          )}

          {/* Instagram — gold to match brand */}
          {member.instagram && (
            <a
              href={member.instagram}
              target="_blank"
              rel="noreferrer"
              style={{
                width: '40px', height: '40px', borderRadius: '50%',
                backgroundColor: GOLD,
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '18px', textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(197,160,89,0.3)',
                transition: 'transform 0.2s, background 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.background = '#d4af6a' }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = GOLD }}
              title="Instagram"
            >
              <i className="fab fa-instagram"></i>
            </a>
          )}

          {/* Facebook — gold to match brand */}
          {member.facebook && (
            <a
              href={member.facebook}
              target="_blank"
              rel="noreferrer"
              style={{
                width: '40px', height: '40px', borderRadius: '50%',
                backgroundColor: GOLD,
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '18px', textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(197,160,89,0.3)',
                transition: 'transform 0.2s, background 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.background = '#d4af6a' }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = GOLD }}
              title="Facebook"
            >
              <i className="fab fa-facebook-f"></i>
            </a>
          )}
        </div>

        {/* Name / role / languages overlay */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(to top, rgba(0,20,50,0.95) 0%, rgba(0,20,50,0.6) 60%, transparent 100%)',
          padding: '40px 20px 20px 20px',
        }}>
          <div style={{
            fontFamily: FONT, fontSize: '18px', fontWeight: 600, color: '#fff',
            marginBottom: '3px',
            transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
            transition: 'transform 0.3s ease',
          }}>
            {member.name}
          </div>
          <div style={{
            fontFamily: FONT, fontSize: '12px', fontWeight: 400,
            color: GOLD, textTransform: 'uppercase', letterSpacing: '0.5px',
            marginBottom: '6px',
            transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
            transition: 'transform 0.3s ease',
          }}>
            {member.role}
          </div>

          {/* Language pills (visible on hover) */}
          {member.languages && member.languages.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '5px',
              opacity: isHovered ? 1 : 0,
              maxHeight: isHovered ? '60px' : '0',
              overflow: 'hidden',
              transition: 'opacity 0.3s ease, max-height 0.3s ease',
              marginBottom: isHovered ? '8px' : '0',
            }}>
              {member.languages.map(lang => (
                <span key={lang} style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: FONT, fontSize: '11px', fontWeight: 500,
                  color: '#fff', letterSpacing: '0.3px',
                  backgroundColor: 'rgba(197,160,89,0.3)',
                  border: '1px solid rgba(197,160,89,0.6)',
                  padding: '4px 10px', borderRadius: '20px',
                  lineHeight: 1, whiteSpace: 'nowrap'
                }}>
                  {lang}
                </span>
              ))}
            </div>
          )}

          {/* Read more cue */}
          <div style={{
            opacity: isHovered ? 1 : 0,
            maxHeight: isHovered ? '20px' : '0',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            fontFamily: FONT, fontSize: '11px', fontWeight: 500,
            color: 'rgba(255,255,255,0.8)',
            display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            Read Full Profile <span style={{ color: GOLD }}>→</span>
          </div>
        </div>
      </div>

      {/* Portal-rendered modal — lives at document.body, unaffected by CSS transforms */}
      {modal}
    </>
  )
}
